import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Trash2, Pencil, MapPin, User, Briefcase } from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import {
  Table,
  Select,
  Modal,
  Form,
  Input,
  Button,
  message,
  Popconfirm,
  Spin,
  Empty,
  Tag,
  Tabs,
  Alert,
  Checkbox,
  Tooltip,
  Pagination,
  Space,
} from "antd";
import { useCommsTemplates, useBulkSendMessage } from "../../../hooks/api/comms/hooks";
import dayjs from "dayjs";
import { JOB_STATUS_CFG } from "../../../constants/jobStatusConfig";
import { useJobList } from "../../../hooks/api/jobs/hooks";
import { IJob, TJobStatus } from "../../../types/api/job";

import {
  IClient,
  IClientCreatePayload,
  TClientType,
} from "../../../types/api/client";

import {
  useClientList,
  useClientCreate,
  useClientUpdate,
  useClientDelete,
  useClientDetail,
  useClientSites,
  useClientSiteCreate,
  useClientSiteUpdate,
  useClientSiteDelete,
  useClientContacts,
  useClientContactCreate,
  useClientContactDelete,
} from "../../../hooks/api/clients/hooks";

import ClientForm from "./ClientForm";
import { useTerm } from "../../../lib/terminology";
import { useStripeRecurringPaymentStatus, useSetupStripeRecurringPayment } from "../../../hooks/api/integrations/stripe-recurring-hooks";

const TYPE_LABEL: Record<TClientType, string> = {
  commercial: "Commercial",
  residential: "Residential",
  public_sector: "Public Sector",
};

const TYPE_COLOR: Record<TClientType, string> = {
  commercial: "blue",
  residential: "green",
  public_sector: "purple",
};

export const TYPE_OPTS = [
  { value: "", label: "All Types" },
  { value: "commercial", label: "Commercial" },
  { value: "residential", label: "Residential" },
  { value: "public_sector", label: "Public Sector" },
];


function displayName(client: IClient) {
  return (
    client.business_name ||
    `${client.first_name ?? ""} ${client.last_name ?? ""}`.trim() ||
    "—"
  );
}

// ── Contacts Manager component ────────────────────────────────────────────────

function ContactsManager({ clientId }: { clientId: number }) {
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm] = Form.useForm();

  const { data: contactsRes, isLoading: contactsLoading } = useClientContacts(clientId);
  const contacts: any[] = (contactsRes?.data as any[]) ?? [];

  const { mutate: createContact, isPending: creatingContact } = useClientContactCreate({
    onSuccess: () => { message.success("Contact added."); setContactModalOpen(false); contactForm.resetFields(); },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? "Failed to add contact."),
  });

  const { mutate: deleteContact } = useClientContactDelete({
    onSuccess: () => message.success("Contact deleted."),
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? "Failed to delete contact."),
  });

  function onContactFinish(values: any) {
    createContact({ clientId, data: values });
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm text-slate-500">{contacts.length} contact{contacts.length !== 1 ? "s" : ""}</span>
        <Button size="small" type="primary" icon={<Plus size={12} />} onClick={() => { contactForm.resetFields(); setContactModalOpen(true); }}>
          Add Contact
        </Button>
      </div>

      {contactsLoading ? (
        <div className="flex justify-center py-8"><Spin /></div>
      ) : contacts.length === 0 ? (
        <Empty description="No contacts yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className="space-y-2">
          {contacts.map((c) => (
            <div key={c.id} className="border border-slate-200 rounded-lg p-3 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <User size={14} className="text-slate-400" />
                  <span className="font-medium text-sm">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</span>
                  {c.is_primary && <Tag color="blue" className="text-xs">Primary</Tag>}
                </div>
                {c.job_title && <div className="text-xs text-slate-500 mt-0.5 ml-5">{c.job_title}</div>}
                <div className="text-xs text-slate-400 mt-0.5 ml-5 space-y-0.5">
                  {c.email && <div>{c.email}</div>}
                  {c.phone && <div>{c.phone}</div>}
                </div>
              </div>
              <Tooltip title="Delete">
                <Popconfirm title="Delete this contact?" okText="Delete" okType="danger" cancelText="Cancel" onConfirm={() => deleteContact({ clientId, contactId: c.id })}>
                  <Button size="small" icon={<Trash2 size={12} />} className="text-slate-500 hover:text-red-600 ml-2" />
                </Popconfirm>
              </Tooltip>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={contactModalOpen}
        onCancel={() => { setContactModalOpen(false); contactForm.resetFields(); }}
        title="Add Contact"
        width="min(440px, 95vw)"
        footer={null}
      >
        <Form form={contactForm} layout="vertical" onFinish={onContactFinish}>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="first_name" label="First Name" rules={[{ required: true, message: "Required" }]}>
              <Input />
            </Form.Item>
            <Form.Item name="last_name" label="Last Name">
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="job_title" label="Job Title">
            <Input placeholder="e.g. Site Manager" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="is_primary" valuePropName="checked" initialValue={false}>
            <Checkbox>Primary contact</Checkbox>
          </Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setContactModalOpen(false); contactForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creatingContact}>Add Contact</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

// ── Sites Manager component ───────────────────────────────────────────────────

function SitesManager({ clientId }: { clientId: number }) {
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<any | null>(null);
  const [siteForm] = Form.useForm();

  const { data: sitesRes, isLoading: sitesLoading } = useClientSites(clientId, { enabled: !!clientId });
  const sites: any[] = (sitesRes?.data as any[]) ?? [];

  const { mutate: createSite, isPending: creatingSite } = useClientSiteCreate(clientId, {
    onSuccess: () => { message.success("Site created."); setSiteModalOpen(false); siteForm.resetFields(); },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? "Failed to create site."),
  });

  const { mutate: updateSite, isPending: updatingSite } = useClientSiteUpdate(clientId, {
    onSuccess: () => { message.success("Site updated."); setSiteModalOpen(false); siteForm.resetFields(); setEditingSite(null); },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? "Failed to update site."),
  });

  const { mutate: deleteSite } = useClientSiteDelete(clientId, {
    onSuccess: () => message.success("Site deleted."),
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? "Failed to delete site."),
  });

  function openAddSite() { setEditingSite(null); siteForm.resetFields(); setSiteModalOpen(true); }
  function openEditSite(site: any) { setEditingSite(site); siteForm.setFieldsValue(site); setSiteModalOpen(true); }
  function closeSiteModal() { setSiteModalOpen(false); setEditingSite(null); siteForm.resetFields(); }

  function onSiteFinish(values: any) {
    if (editingSite) {
      updateSite({ siteId: editingSite.id, data: values });
    } else {
      createSite(values);
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm text-slate-500">{sites.length} site{sites.length !== 1 ? "s" : ""}</span>
        <Button size="small" type="primary" icon={<Plus size={12} />} onClick={openAddSite}>Add Site</Button>
      </div>

      {sitesLoading ? (
        <div className="flex justify-center py-8"><Spin /></div>
      ) : sites.length === 0 ? (
        <Empty description="No sites yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className="space-y-2">
          {sites.map((site) => (
            <div key={site.id} className="border border-slate-200 rounded-lg p-3 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-slate-400" />
                  <span className="font-medium text-sm">{site.name}</span>
                  {site.is_primary && <Tag color="blue" className="text-xs">Primary</Tag>}
                  {!site.is_active && <Tag color="default" className="text-xs">Inactive</Tag>}
                </div>
                <div className="text-xs text-slate-500 mt-1 ml-5">
                  {[site.address_line1, site.city, site.postcode, site.county].filter(Boolean).join(", ")}
                </div>
                {site.access_notes && (
                  <div className="text-xs text-amber-600 mt-0.5 ml-5">Access: {site.access_notes}</div>
                )}
              </div>
              <div className="flex gap-1 ml-2">
                <Tooltip title="Edit">
                  <Button size="small" icon={<Pencil size={12} />} onClick={() => openEditSite(site)} className="text-slate-500 hover:text-blue-600" />
                </Tooltip>
                <Tooltip title="Delete">
                  <Popconfirm title="Delete site?" okText="Delete" okType="danger" cancelText="Cancel" onConfirm={() => deleteSite(site.id)}>
                    <Button size="small" icon={<Trash2 size={12} />} className="text-slate-500 hover:text-red-600" />
                  </Popconfirm>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={siteModalOpen}
        onCancel={closeSiteModal}
        title={editingSite ? "Edit Site" : "Add Site"}
        width="min(480px, 95vw)"
        footer={null}
      >
        <Form form={siteForm} layout="vertical" onFinish={onSiteFinish}>
          <Form.Item name="name" label="Site Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Main Office" />
          </Form.Item>
          <Form.Item name="address_line1" label="Address Line 1">
            <Input />
          </Form.Item>
          <Form.Item name="address_line2" label="Address Line 2">
            <Input />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="city" label="City"><Input /></Form.Item>
            <Form.Item name="county" label="County"><Input /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="postcode" label="Postcode"><Input /></Form.Item>
            <Form.Item name="country" label="Country" initialValue="United Kingdom"><Input /></Form.Item>
          </div>
          <Form.Item name="access_notes" label="Access Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="key_holder" label="Key Holder"><Input /></Form.Item>
            <Form.Item name="alarm_code" label="Alarm Code"><Input /></Form.Item>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={closeSiteModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creatingSite || updatingSite}>
              {editingSite ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

// ── Recurring payment (Stripe saved card) status/action ───────────────────────
// Small, self-contained slot for the client detail modal header — shows the
// saved-card state if one exists, otherwise offers a button to kick off setup.

function RecurringPaymentStatus({ clientId }: { clientId: number }) {
  const { data: statusRes } = useStripeRecurringPaymentStatus(clientId);
  const status = statusRes?.status;
  const { mutate: setupRecurringPayment, isPending } = useSetupStripeRecurringPayment();

  if (status === "active") {
    return (
      <span className="text-sm font-normal bg-green-50 text-green-600 border border-green-200 rounded-full px-3 py-0.5">
        Recurring payment active
      </span>
    );
  }

  if (status === "pending_submission") {
    return (
      <span className="text-sm font-normal bg-amber-50 text-amber-600 border border-amber-200 rounded-full px-3 py-0.5">
        Awaiting client authorization
      </span>
    );
  }

  return (
    <Button
      size="small"
      loading={isPending}
      onClick={() =>
        setupRecurringPayment(clientId, {
          onSuccess: (res: any) => {
            const url = res?.url;
            if (url) {
              window.open(url, "_blank");
            } else {
              message.info("Recurring payment setup started.");
            }
          },
          onError: (err: any) =>
            message.error(err?.response?.data?.message ?? err?.message ?? "Failed to start recurring payment setup."),
        })
      }
    >
      Set up recurring payment
    </Button>
  );
}

// ── Client Jobs Tab ───────────────────────────────────────────────────────────

const STATUS_CFG_JOBS: Record<string, { cls: string; label: string }> = Object.fromEntries(
  Object.entries(JOB_STATUS_CFG).map(([k, v]) => [k, { cls: `${v.bg} ${v.text}`, label: v.label }])
);

function ClientJobsTab({ clientId }: { clientId: number }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const queryData = useMemo(() => ({ page, limit: 10, client_id: clientId }), [page, clientId]);
  const { data: res, isLoading, refetchWithQuery } = useJobList(queryData);
  const jobs: IJob[] = (res?.data as IJob[] | undefined) ?? [];
  const total = Number(res?.extra?.total ?? 0);

  const columns = [
    {
      title: "Job",
      key: "title",
      render: (_: any, record: IJob) => (
        <div>
          <div className="text-sm font-medium text-slate-700 truncate max-w-[180px]">{record.title}</div>
          <div className="text-xs text-slate-400">{record.job_number}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: TJobStatus) => {
        const cfg = STATUS_CFG_JOBS[status] ?? { cls: "bg-slate-100 text-slate-600", label: status };
        return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>{cfg.label}</span>;
      },
    },
    {
      title: "Scheduled",
      dataIndex: "scheduled_start",
      render: (v: string | null) => v ? dayjs(v).format("DD MMM YYYY") : <span className="text-slate-400">—</span>,
    },
    {
      title: "Engineer",
      dataIndex: "assigned_user_name",
      render: (v: string | null) => v || <span className="text-slate-400">Unassigned</span>,
    },
    {
      title: "Value",
      dataIndex: "estimated_value",
      render: (v: number | null) => v ? `£${Number(v).toFixed(2)}` : <span className="text-slate-400">—</span>,
    },
  ];

  if (isLoading) return <div className="flex justify-center py-12"><Spin /></div>;
  if (!jobs.length) return <Empty description="No jobs for this client yet" image={Empty.PRESENTED_IMAGE_SIMPLE} className="py-8" />;

  return (
    <div>
      <Table
        rowKey="id"
        dataSource={jobs}
        columns={columns}
        pagination={false}
        size="small"
        onRow={(record) => ({
          onClick: () => {
            navigate(`/jobs?job_id=${record.id}`);
          },
          className: "cursor-pointer hover:bg-blue-50",
        })}
      />
      {total > 10 && (
        <div className="flex justify-end mt-3">
          <Pagination
            current={page}
            pageSize={10}
            total={total}
            showSizeChanger={false}
            size="small"
            onChange={(p) => { setPage(p); refetchWithQuery({ ...queryData, page: p }); }}
          />
        </div>
      )}
    </div>
  );
}

const ClientsPage = () => {
  const term = useTerm();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<IClient | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("details");
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  const queryData = useMemo(() => {
    const q: any = { page, limit: 20 };
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    if (typeFilter) q.client_type = typeFilter;
    return q;
  }, [page, debouncedSearch, typeFilter]);

const { data: detailRes, isLoading: detailLoading } = useClientDetail(
  editId!,
  {
    enabled: !!editId,
  }
);

  const [form] = Form.useForm();

  const [selectedCountryId, setSelectedCountryId] = useState<
    number | undefined
  >();
  const [selectedStateId, setSelectedStateId] = useState<number | undefined>();

const {
  data: listRes,
  isLoading,
  isError,
  error,
  refetchWithQuery,
} = useClientList(queryData);

  const clients: IClient[] = (listRes?.data as IClient[]) ?? [];
  const totalClients = Number(listRes?.extra?.total ?? clients.length);

  const { mutate: createClient, isPending: creating } = useClientCreate({
    qData: queryData,
    onSuccess: () => {
      message.success("Client created.");
      closeModal();
    },
    onError: (err: any) =>
      message.error(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to create client."
      ),
  });

  const { mutate: updateClient, isPending: updating } = useClientUpdate({
    qData: queryData,
    onSuccess: () => {
      message.success("Client updated.");
      closeModal();
    },
    onError: (err: any) =>
      message.error(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to update client."
      ),
  });

  const { mutate: deleteClient } = useClientDelete({
    qData: queryData,
    onSuccess: () => message.success("Client deleted."),
    onError: (err: any) =>
      message.error(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to delete client."
      ),
  });


  useEffect(() => {
  if (detailRes?.data) {
    const client = detailRes.data;
    form.setFieldsValue({ ...client });
    if (client.country_id) setSelectedCountryId(Number(client.country_id));
    if (client.state_id) setSelectedStateId(Number(client.state_id));
  }
}, [detailRes]);

useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(search);
    setPage(1);
  }, 500);

  return () => clearTimeout(timer);
}, [search]);
useEffect(() => {
  refetchWithQuery(queryData);
}, [queryData]);


  function openCreate() {
    setEditingClient(null);
    form.resetFields();
    setSelectedCountryId(undefined);
    setSelectedStateId(undefined);
    setModalOpen(true);
  }

  function openEdit(client: IClient) {
    setEditingClient(client);
    setEditId(client.id);
    form.setFieldsValue({ ...client });
    setSelectedCountryId(client.country_id ? Number(client.country_id) : undefined);
    setSelectedStateId(client.state_id ? Number(client.state_id) : undefined);
    setModalOpen(true);
  }

function closeModal() {
  setModalOpen(false);
  setEditingClient(null);
  setEditId(null);
  setActiveTab("details");
  form.resetFields();
}

  function onFinish(values: any) {
    if (editingClient) {
      updateClient({ id: editingClient.id, ...values });
    } else {
      createClient(values as IClientCreatePayload);
    }
  }

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_: any, record: IClient) => displayName(record),
    },
    {
      title: "Type",
      dataIndex: "client_type",
      render: (type: TClientType) => (
        <Tag color={TYPE_COLOR[type]}>{TYPE_LABEL[type]}</Tag>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
    },
    {
      title: "Account Ref",
      dataIndex: "account_reference",
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding_balance",
      key: "outstanding_balance",
      width: 120,
      render: (val: any) =>
        val && Number(val) > 0 ? (
          <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
            £{Number(val).toFixed(2)}
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: IClient) => (
        <div className="flex gap-1">
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<Pencil size={14} />}
              onClick={() => openEdit(record)}
              className="text-slate-500 hover:text-blue-600"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this client?"
              description="Their jobs and invoices will remain but the client record will be removed."
              onConfirm={() => deleteClient({ ids: String(record.id) })}
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
            >
              <Button size="small" icon={<Trash2 size={14} />} className="text-slate-500 hover:text-red-600" />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  const outstandingBalance = Number((detailRes?.data as any)?.outstanding_balance ?? 0);

  const rowSelection = {
    selectedRowKeys: selectedClientIds,
    onChange: (keys: React.Key[]) => setSelectedClientIds(keys as number[]),
  };

  // ── Bulk Send Modal ─────────────────────────────────────────────────────────
  function BulkSendModal() {
    const [channel, setChannel] = useState("email");
    const [msgText, setMsgText] = useState("");
    const { mutate: bulkSend, isPending } = useBulkSendMessage();
    const { data: templatesData } = useCommsTemplates();
    const templates: any[] = templatesData?.data ?? [];

    return (
      <Modal
        open={bulkSendOpen}
        title={`Send message to ${selectedClientIds.length} client${selectedClientIds.length !== 1 ? "s" : ""}`}
        onCancel={() => setBulkSendOpen(false)}
        footer={null}
        width={480}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle" className="mt-3">
          <Select value={channel} onChange={setChannel} style={{ width: "100%" }}>
            <Select.Option value="email">Email</Select.Option>
            <Select.Option value="sms" disabled>SMS (Coming Soon)</Select.Option>
            <Select.Option value="whatsapp" disabled>WhatsApp (Coming Soon)</Select.Option>
          </Select>

          {templates.length > 0 && (
            <Select
              placeholder="Use a template (optional)"
              style={{ width: "100%" }}
              onChange={(val) => {
                const t = templates.find((tmpl: any) => tmpl.id === val);
                if (t) setMsgText(t.content);
              }}
              allowClear
            >
              {templates.map((t: any) => (
                <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>
              ))}
            </Select>
          )}

          <Input.TextArea
            rows={5}
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            placeholder="Your message…"
          />

          <Space>
            <Button onClick={() => setBulkSendOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              loading={isPending}
              disabled={!msgText.trim()}
              onClick={() => {
                bulkSend(
                  { client_ids: selectedClientIds, message: msgText, channel },
                  {
                    onSuccess: (res: any) => {
                      const q = res.data?.data?.queued ?? selectedClientIds.length;
                      message.success(`Messages queued for ${q} clients`);
                      setBulkSendOpen(false);
                      setSelectedClientIds([]);
                      setMsgText("");
                    },
                    onError: () => message.error("Failed to send messages"),
                  }
                );
              }}
            >
              Send to {selectedClientIds.length} client{selectedClientIds.length !== 1 ? "s" : ""}
            </Button>
          </Space>
        </Space>
      </Modal>
    );
  }

  return (
    <div className="min-h-screen">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold">{term("client", true)}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage your clients, sites and contacts</p>
        </div>

        <ButtonWrapper type="primary" icon={<Plus size={16} />} onClick={openCreate} className="flex items-center gap-2">
          Add {term("client")}
        </ButtonWrapper>
      </div>

      {/* FILTERS */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2 text-gray-400" size={16} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="pl-9"
          />
        </div>

        <Select
          value={typeFilter}
          onChange={(v) => { setTypeFilter(v); setPage(1); }}
          options={TYPE_OPTS}
          className="w-48"
        />
      </div>

      {/* BULK SELECT ALERT */}
      {selectedClientIds.length > 0 && (
        <Alert
          type="info"
          message={`${selectedClientIds.length} client${selectedClientIds.length !== 1 ? "s" : ""} selected`}
          action={
            <Space>
              <Button size="small" onClick={() => setBulkSendOpen(true)}>Send Message</Button>
              <Button size="small" onClick={() => setSelectedClientIds([])}>Clear</Button>
            </Space>
          }
          style={{ marginBottom: 8 }}
        />
      )}

      {/* TABLE */}
      {isError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load data"
            description={(error as any)?.message ?? "Please refresh the page or contact support."}
          />
        </div>
      )}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spin size="large" />
        </div>
      ) : clients.length === 0 ? (
        <Empty description={<span className="text-slate-500">No clients yet.</span>}>
          <Button type="primary" onClick={openCreate}>Add your first client</Button>
        </Empty>
      ) : (
        <Table
          rowKey="id"
          dataSource={clients}
          columns={columns}
          rowSelection={rowSelection}
          pagination={{
            current: page,
            pageSize: 20,
            total: totalClients,
            showSizeChanger: false,
            showTotal: (t) => `${t} clients`,
            onChange: (p) => { setPage(p); refetchWithQuery({ ...queryData, page: p }); },
          }}
        />
      )}

      <BulkSendModal />

      {/* MODAL */}
      <Modal
        open={modalOpen}
        onCancel={closeModal}
        title={
          <div className="flex items-center justify-between pr-8">
            <span>{editingClient ? `Edit ${term("client")}` : `Add ${term("client")}`}</span>
            <div className="flex items-center gap-2">
              {editingClient && outstandingBalance > 0 && (
                <span className="text-sm font-normal bg-red-50 text-red-600 border border-red-200 rounded-full px-3 py-0.5">
                  Outstanding: £{outstandingBalance.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
              {editingClient && outstandingBalance === 0 && detailRes?.data && (
                <span className="text-sm font-normal bg-green-50 text-green-600 border border-green-200 rounded-full px-3 py-0.5">
                  No outstanding balance
                </span>
              )}
              {editingClient && <RecurringPaymentStatus clientId={editingClient.id} />}
            </div>
          </div>
        }
        width="min(640px, 95vw)"
        footer={null}
      >
        {editingClient ? (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="small"
            items={[
              {
                key: "details",
                label: "Details",
                children: (
                  <Form form={form} layout="vertical" onFinish={onFinish}>
                    <ClientForm
                      form={form}
                      isEdit={!!editingClient}
                      selectedCountryId={selectedCountryId}
                      setSelectedCountryId={setSelectedCountryId}
                      selectedStateId={selectedStateId}
                      setSelectedStateId={setSelectedStateId}
                    />
                    <div className="flex justify-end gap-2 mt-4">
                      <Button onClick={closeModal}>Cancel</Button>
                      <Button type="primary" htmlType="submit" loading={creating || updating}>
                        Update
                      </Button>
                    </div>
                  </Form>
                ),
              },
              {
                key: "sites",
                label: "Sites",
                children: editingClient ? <SitesManager clientId={editingClient.id} /> : null,
              },
              {
                key: "contacts",
                label: "Contacts",
                children: editingClient ? <ContactsManager clientId={editingClient.id} /> : null,
              },
              {
                key: "jobs",
                label: <span className="flex items-center gap-1"><Briefcase size={12} />Jobs</span>,
                children: editingClient ? <ClientJobsTab clientId={editingClient.id} /> : null,
              },
            ]}
          />
        ) : (
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <ClientForm
              form={form}
              isEdit={false}
              selectedCountryId={selectedCountryId}
              setSelectedCountryId={setSelectedCountryId}
              selectedStateId={selectedStateId}
              setSelectedStateId={setSelectedStateId}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={closeModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creating || updating}>
                Create
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default ClientsPage;